import { computed, inject, Injectable } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { createAuthConfig } from '../config/auth.config';
import { environment } from '../../environments/environment';
import {
  COUNTDOWN_STORAGE_KEY,
  COUNTDOWN_STORAGE_PAUSED_KEY,
  COUNTDOWN_STORAGE_TIMESTAMP_KEY,
} from '../config/constants';
import { RouterService } from './router.service';
import { Route } from '../enum/route.enum';

interface UserClaims {
  name?: string;
  preferred_username?: string;
  email?: string;
  given_name?: string;
  family_name?: string;

  [key: string]: unknown;
}

const LOCAL_BYPASS_SESSION_KEY = 'local_auth_bypass_enabled';

@Injectable({ providedIn: 'root' })
export class AuthService {
  readonly oAuthService = inject(OAuthService);
  readonly routerService = inject(RouterService);
  private readonly localBypassAvailable = environment.local && environment.oauth.useLocalConfig;

  private get useLocalAuthBypass(): boolean {
    return this.localBypassAvailable && sessionStorage.getItem(LOCAL_BYPASS_SESSION_KEY) === 'true';
  }

  readonly isAuthenticated = computed(() => {
    return this.useLocalAuthBypass || this.oAuthService.hasValidAccessToken();
  });

  isLocalBypassAvailable(): boolean {
    return this.localBypassAvailable;
  }

  enableLocalBypass(): void {
    if (this.localBypassAvailable) {
      sessionStorage.setItem(LOCAL_BYPASS_SESSION_KEY, 'true');
    }
  }

  async initializeAuth(): Promise<void> {
    if (this.useLocalAuthBypass) {
      return;
    }

    try {
      const authConfig = createAuthConfig(environment.oauth.issuer, environment.oauth.clientId);
      authConfig.redirectUri = `${window.location.origin}${this.routerService.getCallbackRoute()}`;
      authConfig.postLogoutRedirectUri = `${window.location.origin}/${this.routerService.getLoginRoute()}`;

      this.oAuthService.configure(authConfig);
      await this.oAuthService.loadDiscoveryDocument();
      this.oAuthService.setupAutomaticSilentRefresh();
    } catch (error) {
      console.error('Failed to initialize auth:', error);
      throw error;
    }
  }

  initLoginFlow(additionalState?: string): void {
    this.oAuthService.initLoginFlow(additionalState);
  }

  hasValidAccessToken(): boolean {
    return this.useLocalAuthBypass || this.oAuthService.hasValidAccessToken();
  }

  getState(): string | undefined {
    return this.oAuthService.state;
  }

  getUserInfo(): UserClaims | null {
    if (this.useLocalAuthBypass) {
      return {
        name: 'Local User',
        preferred_username: 'local.user@example.com',
        given_name: 'Local',
        family_name: 'User',
      };
    }

    if (!this.hasValidAccessToken()) return null;
    return this.oAuthService.getIdentityClaims() as UserClaims;
  }

  logOut(): void {
    try {
      // Set countdown to paused state directly in localStorage to preserve remaining time
      const stored = localStorage.getItem(COUNTDOWN_STORAGE_KEY);
      if (stored) {
        localStorage.setItem(COUNTDOWN_STORAGE_PAUSED_KEY, 'true');
        localStorage.setItem(COUNTDOWN_STORAGE_TIMESTAMP_KEY, Date.now().toString());
        console.log('AuthService: Countdown paused on logout');
      }

      this._clearTokenStorage();
      this.routerService.navigateTo(Route.LOGIN);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }

  private _clearTokenStorage(): void {
    const tokenKeys = [
      'access_token',
      'id_token',
      'refresh_token',
      'id_token_expires_at',
      'access_token_expires_at',
      'id_token_claims_obj',
      'access_token_stored_at',
      'id_token_stored_at',
    ];

    tokenKeys.forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });

    // Delete OAuth-specific keys
    localStorage.removeItem('nonce');
    localStorage.removeItem('PKCE_verifier');
    localStorage.removeItem('discovery_document');
    sessionStorage.removeItem('nonce');
    sessionStorage.removeItem('PKCE_verifier');
    sessionStorage.removeItem('discovery_document');
    sessionStorage.removeItem(LOCAL_BYPASS_SESSION_KEY);
  }
}
